/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { responsiveStylesFn } from '@zapier/held-tasks-banner/src/utils/responsiveStyles';
import buildStaticAssetUrl from '@zapier/held-tasks-banner/src/utils/buildStaticAssetUrl';

import {
  Animation,
  Button,
  Colors,
  Icon,
  IconButton,
  Img,
  Shadows,
  Text,
} from '@zapier/design-system';
import { useUniversalNavContext } from '@zapier/universal-nav';

import useSize from '@zapier/held-tasks-banner/src/hooks/useSize';

import { UseQuery } from './types';
import HeldTaskStepItem from './HeldTaskStepItem';
import useHeldTasks from './useHeldTasks';

type Props = {
  accountId: number;
  currentUrl: string;
  useQuery: UseQuery;
};

const buttonStyle = css`
  justify-self: center;
`;

const detailsStyle = css`
  display: grid;
`;

const imageStyle = css`
  justify-self: center;
  width: 50px;
`;

const innerStyle = css`
  margin: 0 auto;
  max-width: 1120px;
  padding: 0 20px;
`;

const listStyle = responsiveStylesFn({
  medium: css`
    margin-left: 70px;
  `,
  small: css`
    display: grid;
    grid-gap: 3px;
    list-style-position: inside;
    list-style-type: decimal;
    margin-top: 20px;
  `,
});

const rootStyle = ({ sidebarWidth }: { sidebarWidth: number }) => [
  css`
    background-color: ${Colors.grey000};
    box-shadow: ${Shadows.elevation3};
    padding: 10px 20px;
    padding-left: calc(${sidebarWidth}px + 20px);
    transition: ${Animation.transitionValue};
    z-index: 1;
  `,
];

const subStyle = responsiveStylesFn({
  medium: css`
    grid-template-columns: auto 1fr;
    justify-content: flex-start;
  `,
  small: css`
    align-items: center;
    display: grid;
    grid-gap: 5px;
    justify-content: center;
  `,
});

const topStyle = responsiveStylesFn({
  medium: css`
    grid-template-columns: 50px 1fr auto;
  `,
  small: css`
    align-items: center;
    display: grid;
    grid-gap: 20px;
  `,
});

function HeldTasksBanner({ accountId, currentUrl, useQuery }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    buttonText,
    daysRemainText,
    isHidden,
    msg,
    numOfStepsDone,
    steps,
    title,
  } = useHeldTasks({ accountId, currentUrl, useQuery });
  const size = useSize();
  const { sidebarWidth } = useUniversalNavContext();

  function toggleIsOpen() {
    setIsOpen(!isOpen);
  }

  if (isHidden) return null;

  return (
    <div css={rootStyle({ sidebarWidth })}>
      <div css={innerStyle}>
        <div css={topStyle(size)}>
          <div css={imageStyle}>
            <Img
              alt="Zapling"
              src={buildStaticAssetUrl('images/zaplings/zapling-held.svg')}
            />
          </div>
          <div css={detailsStyle}>
            <Text
              textAlign={size === 'small' ? 'center' : 'left'}
              type="subtitle800"
            >
              {title}
            </Text>
            <span css={subStyle(size)}>
              {size !== 'small' && <Icon name="miscClock" size={19} />}
              <Text
                textAlign={size === 'small' ? 'center' : 'left'}
                type="altText900"
              >
                <strong>{daysRemainText}</strong> {msg}
              </Text>
            </span>
          </div>
          <div css={buttonStyle}>
            {isOpen ? (
              <IconButton icon="arrowSmallUp" onClick={toggleIsOpen} />
            ) : (
              <Button color="orange" onClick={toggleIsOpen}>
                {buttonText}
              </Button>
            )}
          </div>
        </div>
        {isOpen && steps.length > 0 && (
          <ol css={listStyle(size)}>
            {steps.map(step => (
              <HeldTaskStepItem {...step} numOfStepsDone={numOfStepsDone} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export default HeldTasksBanner;
